import React, {useCallback, useMemo, useState} from "react";
import {Box, Card, CardContent, CardMedia, FormControlLabel, Switch, Typography} from "@mui/material";
import ReactECharts from "echarts-for-react";
import PageLayout from "../components/layout/PageLayout";
import ReportFilters from "../components/reports/ReportFilters";
import cashFlowPreview from "../assets/reports/cash-flow.svg";
import {useTranslation} from "react-i18next";

const formatLabel = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-GB", {day: "numeric", month: "short"}).format(date);
};

const ReportsCashFlowBalancesPage = () => {
    const {t} = useTranslation();
    const [groupByCurrency, setGroupByCurrency] = useState(false);
    const [reportResponses, setReportResponses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const formatPeriodRange = useCallback((period) => {
        const from = period?.dateFrom ? new Date(period.dateFrom) : null;
        const to = period?.dateTo ? new Date(period.dateTo) : null;
        if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
            return t("common.labels.period");
        }
        const formatter = new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
        return `${formatter.format(from)} - ${formatter.format(to)}`;
    }, []);

    const handleApply = useCallback(async (request) => {
        const rawPeriods = request?.periods || [];
        if (!rawPeriods.length) {
            setLoadError(t("reports.messages.selectPeriodRange"));
            setReportResponses([]);
            return;
        }
        setIsLoading(true);
        setLoadError(null);
        try {
            const responseList = await Promise.all(
                rawPeriods.map(async (period, index) => {
                    const response = await fetch("/api/reports/cash-flow", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                            ...request,
                            groupByCurrency,
                            periods: [period]
                        })
                    });
                    if (!response.ok) {
                        throw new Error(`Failed to load report (${response.status})`);
                    }
                    const json = await response.json();
                    return {
                        key: `period-${index}`,
                        label: formatPeriodRange(period),
                        data: json
                    };
                })
            );
            setReportResponses(responseList);
        } catch (error) {
            console.log(error);
            setLoadError(error?.message || t("reports.messages.failedToLoadReport"));
            setReportResponses([]);
        } finally {
            setIsLoading(false);
        }
    }, [formatPeriodRange, groupByCurrency]);

    const handleReset = useCallback(() => {
        setReportResponses([]);
        setLoadError(null);
    }, []);

    const chartOptions = useMemo(() => {
        return reportResponses.map((response) => {
            const series = response.data?.series || [];
            if (!series.length) {
                return null;
            }
            const labels = series[0]?.days?.map((day) => formatLabel(day.date)) || [];
            const chartSeries = series.map((entry) => ({
                name: entry.name,
                type: "line",
                smooth: true,
                data: (entry.days || []).map((day) => Number(day?.closingBalance || 0))
            }));
            return {
                key: response.key,
                label: response.label,
                option: {
                    tooltip: {
                        trigger: "axis"
                    },
                    legend: {
                        type: "scroll",
                        bottom: 0
                    },
                    grid: {
                        left: 40,
                        right: 20,
                        top: 40,
                        bottom: 80
                    },
                    xAxis: {
                        type: "category",
                        data: labels
                    },
                    yAxis: {
                        type: "value"
                    },
                    series: chartSeries
                }
            };
        }).filter(Boolean);
    }, [reportResponses]);

    return (
        <PageLayout title={t("pages.reports.cashFlow.balancesTitle")}>
            <CardContent sx={{minHeight: 200, maxHeight: "90%", overflowY: "auto"}}>
                <ReportFilters
                    showTypeTabs={false}
                    showCategories={false}
                    showBudgets={false}
                    includeReportTypeFilter={false}
                    onApply={handleApply}
                    onReset={handleReset}
                />
                <FormControlLabel
                    sx={{mb: 2}}
                    control={
                        <Switch
                            checked={groupByCurrency}
                            onChange={(event) => setGroupByCurrency(event.target.checked)}
                            color="primary"
                        />
                    }
                    label={t("reports.groupByCurrency")}
                />
                {isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t("reports.messages.loading")}
                    </Typography>
                )}
                {loadError && (
                    <Typography variant="body2" color="error" sx={{mb: 2}}>
                        {loadError}
                    </Typography>
                )}
                {chartOptions.length ? (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2, width: "100%"}}>
                        {chartOptions.map((entry) => (
                            <Card key={entry.key} sx={{maxWidth: 1100, width: "100%", margin: "0 auto"}}>
                                <CardContent>
                                    <Typography variant="subtitle1" sx={{mb: 1}}>
                                        {entry.label}
                                    </Typography>
                                    <Box sx={{height: 520, width: "100%"}}>
                                        <ReactECharts option={entry.option} style={{height: "100%", width: "100%"}} />
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                ) : (
                    <Card sx={{maxWidth: 1100, margin: "0 auto"}}>
                        <CardMedia
                            component="img"
                            image={cashFlowPreview}
                            alt={t("pages.reports.cashFlow.cards.balances.alt")}
                        />
                    </Card>
                )}
            </CardContent>
        </PageLayout>
    );
};

export default ReportsCashFlowBalancesPage;
